/* eslint-disable @typescript-eslint/no-unused-vars */
import Pagination from "components/Pagination";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { usePopupsState } from "hooks";
import { useLinks } from "hooks/useLinks";
import { IFriendReferral, IFriendReferralQualified } from "interfaces";
import { FeatureType } from "interfaces/feature-tracker";
import { Subtitle } from "pages/FriendCenter/FriendShop/styles";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	featureTrackerAPI,
	useGetFriendReferralQualifiedQuery
} from "redux/apis";
import FriendItem from "./FriendItem";
import NoFriendsState from "./NoFriendsState";
import { FriendSelectionSkeleton } from "./skeleton";
import { Container, CustomButton } from "./styles";

const FRIENDS_PER_PAGE = 6;

const FriendSelectionReferral = () => {
	const navigate = useNavigate();
	const { friend_center } = useLinks();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { authToken } = useWebStoreContext();
	const { isAnyPopupOpen } = usePopupsState();
	const [currentPage, setCurrentPage] = useState(1);
	const {
		data: friendsData,
		isLoading: friendsIsLoading
	}: { data?: IFriendReferralQualified; isLoading: boolean } =
		useGetFriendReferralQualifiedQuery(undefined, {
			skip:
				window.location.pathname !==
					`${friend_center.path}/invite/progress/friends` || document.hidden,
			pollingInterval: 1000 * 60,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: false
		});
	const friends = friendsData?.users ?? [];
	const totalPages = Math.ceil(friends.length / FRIENDS_PER_PAGE);
	const paginatedFriends = useMemo(() => {
		const start = (currentPage - 1) * FRIENDS_PER_PAGE;
		return friends.slice(start, start + FRIENDS_PER_PAGE);
	}, [friends, currentPage]);

	const {
		data: referralTrackerData,
		isLoading: referralTrackerIsLoading
	}: { data?: IFriendReferral; isLoading: boolean } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState(
			{
				FeatureType: FeatureType.FRIEND_REFERRAL,
				isAuthenticated: !!authToken?.value
			} /* ,
			{
				skip:
					document.hidden ||
					isAnyPopupOpen ||
					window.location.pathname !==
						`${friend_center.path}/invite/progress/friends`,
				pollingInterval: 1000 * 60,
				refetchOnFocus: true
			} */
		);

	const navigateBack = () => {
		navigate(`${friend_center.path}/invite/progress`);
	};

	const handleBack = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "specific_friend_invite_button_click",
			event_type: "friend_list",
			action: "back_button_click",
			source: "web_friend_referral"
		});
		navigateBack();
	};

	useEffect(() => {
		if (totalPages > 0 && currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	useEffect(() => {
		if (!referralTrackerIsLoading && !referralTrackerData?.tracker) {
			navigateBack();
		}
	}, [referralTrackerData, referralTrackerIsLoading]);

	useEffect(() => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "friend_list_shown",
			event_type: "friend_list",
			action: "show",
			source: "web_friend_referral"
		});
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;
	}, []);

	if (!friendsData || friendsIsLoading) {
		return (
			<>
				<Subtitle>Get Rewards for each person that joins the web!</Subtitle>
				<FriendSelectionSkeleton />
			</>
		);
	}

	return (
		<>
			{friends.length !== 0 && (
				<Subtitle>Get Rewards for each person that joins the web!</Subtitle>
			)}
			<Container>
				{paginatedFriends.map((friend) => {
					return <FriendItem key={friend.userId} data={friend} />;
				})}
			</Container>
			{/* add no friends state */}
			{friends.length === 0 && <NoFriendsState />}
			<Pagination
				totalItems={friends.length}
				pageSize={FRIENDS_PER_PAGE}
				page={currentPage}
				onPageChange={setCurrentPage}
			/>
			<CustomButton themeColor="secondary" onClick={handleBack}>
				Back
			</CustomButton>
		</>
	);
};

export default FriendSelectionReferral;
